import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { auth, db, handleFirestoreError, OperationType } from "./firebase";
import { UserProfile } from "../types";

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  hasClubCard: boolean;
  loading: boolean;
  isAuthReady: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  hasClubCard: false,
  loading: true,
  isAuthReady: false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [hasClubCard, setHasClubCard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;
    let unsubscribeClub: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("Auth state changed:", firebaseUser?.email || "No user");
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // Listen to profile changes in real-time
        const profileRef = doc(db, "users", firebaseUser.uid);
        unsubscribeProfile = onSnapshot(profileRef, async (snapshot) => {
          if (snapshot.exists()) {
            console.log("Profile loaded for:", firebaseUser.email);
            const data = snapshot.data() as UserProfile;
            const isKensu = firebaseUser.email?.toLowerCase().includes("kensudev") || firebaseUser.email?.toLowerCase().includes("kensu");
            const isAdminEmail = firebaseUser.email === "admin@mallempremarket.com";
            if (isKensu || isAdminEmail) {
              data.role = "admin";
            }
            setProfile(data);
            // If the user document exists, we can consider auth state for loading purposes partially done
            // but we still wait for club listener for a full ready state
          } else {
            console.log("Creating new profile for:", firebaseUser.email);
            const userEmail = firebaseUser.email || "";
            const isKensu = userEmail.toLowerCase().includes("kensudev");
            const isAdminEmail = userEmail === "admin@mallempremarket.com";
            
            const newProfile: UserProfile = {
              uid: firebaseUser.uid,
              email: userEmail,
              displayName: firebaseUser.displayName || "Usuario",
              role: (isAdminEmail || isKensu) ? "admin" : "buyer",
              photoURL: firebaseUser.photoURL || undefined,
              createdAt: new Date().toISOString(),
            };
            try {
              await setDoc(profileRef, newProfile);
              console.log("New profile created successfully");
            } catch (err) {
              console.error("Error creating profile:", err);
              try {
                handleFirestoreError(err, OperationType.WRITE, `users/${firebaseUser.uid}`);
              } catch (e) { /* silent throw for protocol */ }
            }
          }
        }, (err) => {
          console.error("Profile onSnapshot error:", err);
          setLoading(false);
          setIsAuthReady(true);
          try {
            handleFirestoreError(err, OperationType.GET, `users/${firebaseUser.uid}`);
          } catch (e) { /* silent */ }
        });

        // Listen to club membership
        const clubRef = doc(db, "mall_club", firebaseUser.uid);
        unsubscribeClub = onSnapshot(clubRef, (snapshot) => {
          console.log("Club status loaded:", snapshot.exists());
          setHasClubCard(snapshot.exists());
          setLoading(false);
          setIsAuthReady(true);
        }, (err) => {
          console.warn("Club card listener failed, assuming no card.");
          setHasClubCard(false);
          setLoading(false);
          setIsAuthReady(true);
          try {
            handleFirestoreError(err, OperationType.GET, `mall_club/${firebaseUser.uid}`);
          } catch (e) { /* silent */ }
        });

      } else {
        console.log("No user session found");
        if (unsubscribeProfile) unsubscribeProfile();
        if (unsubscribeClub) unsubscribeClub();
        setProfile(null);
        setHasClubCard(false);
        setLoading(false);
        setIsAuthReady(true);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
      if (unsubscribeClub) unsubscribeClub();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, hasClubCard, loading, isAuthReady }}>
      {children}
    </AuthContext.Provider>
  );
}
